import { AutomationRunStatus } from "@redotech/crdb-enums/automations/automation-run-status";
import { AutomationStatus } from "@redotech/crdb-enums/automations/automation-status";
import { AutomationVersionStrategy } from "@redotech/crdb-enums/automations/automation-version-strategy";
import {
  automationDefinitionSchema,
  jsonSchema,
} from "@redotech/redo-automations-model/graph/automation";
import * as z from "zod";

export const diagnosticSchema = z.object({
  severity: z.enum(["error", "warning"]),
  code: z.string(),
  message: z.string(),
  nodeId: z.string().optional(),
  bindingKey: z.string().optional(),
});
export type AutomationDiagnostic = z.infer<typeof diagnosticSchema>;

/**
 * A definition sent for checking, testing or saving. Exploring and simulating
 * run on the request thread, so the size is capped well below the body limit.
 */
export const DEFINITION_LIMITS = {
  nodes: 500,
  edges: 1000,
  characters: 1_000_000,
} as const;

/** Serialized length, or Infinity for what can't be serialized (cycles, too deep). */
export function serializedLength(value: unknown): number {
  try {
    return JSON.stringify(value)?.length ?? 0;
  } catch {
    return Infinity;
  }
}

const definitionShapeSchema = z.object({
  nodes: z.array(z.unknown()).max(DEFINITION_LIMITS.nodes).optional(),
  edges: z.array(z.unknown()).max(DEFINITION_LIMITS.edges).optional(),
});

/** Any JSON within DEFINITION_LIMITS; whether it's an automation is the handler's to say. */
export const definitionInputSchema = z.unknown().superRefine((value, ctx) => {
  if (serializedLength(value) > DEFINITION_LIMITS.characters) {
    ctx.addIssue({
      code: "custom",
      message: `A definition can be at most ${DEFINITION_LIMITS.characters} characters`,
    });
    return;
  }
  const shape = definitionShapeSchema.safeParse(value);
  if (!shape.success) {
    for (const issue of shape.error.issues) {
      if (issue.code === "too_big") {
        ctx.addIssue({
          code: "custom",
          path: issue.path,
          message: `A definition can have at most ${DEFINITION_LIMITS.nodes} steps and ${DEFINITION_LIMITS.edges} connections`,
        });
      }
    }
  }
});

export const automationSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  /** The trigger it runs on: the published version's, or the draft's before the first publish. */
  triggerKind: z.string(),
  status: z.enum(AutomationStatus),
  publishedVersion: z.number().int().nullable(),
  /** Whether runs move to the latest published version after a wait, or stay on the one they started on. */
  versionStrategy: z.enum(AutomationVersionStrategy),
  createdBy: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type AutomationSummary = z.infer<typeof automationSummarySchema>;

/** An automation with its working copy, as the editor loads it. */
export const automationDetailSchema = automationSummarySchema.extend({
  draft: automationDefinitionSchema,
  /** Pass back with the next save or publish; it changes whenever the draft does. */
  draftRevision: z.string(),
});
export type AutomationDetail = z.infer<typeof automationDetailSchema>;

export const draftResultSchema = z.object({
  automation: automationDetailSchema,
  /** Validation of the saved draft; errors block publishing, not saving. */
  diagnostics: z.array(diagnosticSchema),
});

export const versionSummarySchema = z.object({
  automationId: z.string(),
  version: z.number().int(),
  createdBy: z.string().nullable(),
  createdAt: z.date(),
});

export const versionDetailSchema = versionSummarySchema.extend({
  definition: automationDefinitionSchema,
});

export const triggerOrderSchema = z.object({
  triggerKind: z.string(),
  /** Automation ids in the order runs are dispatched. */
  automationIds: z.array(z.string()),
  /** Pass back as `expectedVersion` when reordering. */
  version: z.number().int(),
});

export const effectResultSchema = z.union([
  z.object({ ok: z.literal(true), value: jsonSchema }),
  z.object({
    ok: z.literal(false),
    error: z.object({
      message: z.string(),
      code: z.string().optional(),
      retryable: z.boolean().optional(),
    }),
  }),
]);

/** One step a run visited: whether it ran, the port it took and what it did. */
export const stepRecordSchema = z.object({
  nodeId: z.string(),
  kind: z.string(),
  startedAt: z.string(),
  finishedAt: z.string().optional(),
  outcome: z.enum(["completed", "disabled", "failed", "cancelled"]).optional(),
  port: z.string().optional(),
  inputs: z.record(z.string(), jsonSchema).optional(),
  output: jsonSchema.optional(),
  note: z.string().optional(),
  effects: z.array(
    z.object({
      type: z.string(),
      input: jsonSchema,
      result: effectResultSchema,
    }),
  ),
});
export type StepRecord = z.infer<typeof stepRecordSchema>;

export const runSummarySchema = z.object({
  id: z.string(),
  automationId: z.string(),
  version: z.number().int(),
  triggerKind: z.string(),
  status: z.enum(AutomationRunStatus),
  /** The strategy the run started with; later changes to the automation don't affect it. */
  versionStrategy: z.enum(AutomationVersionStrategy),
  /** The event a waiting run is parked on. */
  waitEvent: z.string().nullable(),
  startedAt: z.date(),
  updatedAt: z.date(),
  finishedAt: z.date().nullable(),
});

export const runDetailSchema = runSummarySchema.extend({
  steps: z.array(stepRecordSchema),
  failure: z
    .object({ nodeId: z.string().nullable(), message: z.string() })
    .nullable(),
  /** Why a rejected run didn't start, or why a cancelled one was stopped. */
  rejection: z.string().nullable(),
});
